import { useEffect, useState } from 'react';
import {
  IdentityTypeEnum,
  IUltimateBeneficiary,
} from '../../../../models/onboarding.model';
import { useFormikContext } from 'formik';
import AppInput from '../../../../components/base/input';
import AppDatePicker from '../../../../components/base/date-picker';
import PhoneNumberInput from '../../../../components/base/phone-input-tel';
import CustomDropdown from '../../../../components/base/custom-dropdown';
import {
  useOnboardingStore,
  useRateStore,
  useResourceStore,
} from '../../../../store';
import toast from 'react-hot-toast';
import { isYoungerThan18 } from '../../../../utils/dates.util';
import GooglePlaceAutoComplete from '../../../../components/base/google-place-auto-complete';
import { parseAddressComponents } from '../../../../utils/address.util';
import {
  toTitleCaseFromUnderscore,
  toUnderscoreFromTitleCase,
} from '../../../../utils/string.util';
import BaseInfo from '../../../../components/base/info';
import BaseRadioGroupNew from '../../../../components/base/radio-group-new';
import AppFileUpload from '../../../../components/base/app-file-upload';
import { IFile } from '../../../../models/document.model';
import { IActiveCountry } from '../../../../models/rate.model';

const radioOptions = [
  {
    label: 'Identity Number',
    value: IdentityTypeEnum.Id_Number,
  },
  {
    label: 'Identity Document',
    value: IdentityTypeEnum.Id_Document,
  },
];

interface SingleUltimateBeneficiaryProps {
  beneficiaryIndex: number;
}

export const SingleUltimateBeneficiary = ({
  beneficiaryIndex,
}: SingleUltimateBeneficiaryProps) => {
  const formik = useFormikContext<IUltimateBeneficiary>();

  const { setUltimateBeneficiaryByIndex } = useOnboardingStore();
  const {
    identityDocumentTypes,
    getAllCountryStates,
    countryStates,
    loading: loadingResources,
  } = useResourceStore();
  const { activeCountries: countries } = useRateStore();

  const [mobileNumberIsOkay, setMobileNumberIsOkay] = useState(false);
  const [mobileNumberIsTouched, setMobileNumberIsTouched] = useState(false);

  const filteredCountries = countries.filter(
    (country) =>
      country.name === 'Nigeria' ||
      country.name === 'Ghana' ||
      country.name === 'Senegal' ||
      country.name === 'Cameroon',
  );

  const [manualEntry, setManualEntry] = useState(true);

  const handleDobDateChange = (date: Date | null) => {
    if (isYoungerThan18(date)) {
      setUltimateBeneficiaryByIndex(
        {
          ...formik.values,
          birthDate: null,
        },
        beneficiaryIndex,
      );
      return toast.error('Date of birth can not be younger than 18');
    }

    setUltimateBeneficiaryByIndex(
      {
        ...formik.values,
        birthDate: date,
      },
      beneficiaryIndex,
    );
  };

  const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
    const address = parseAddressComponents(place);

    const { activeCountries: countries } = useRateStore.getState();
    const uboCountry = countries.find(
      (item) => item.name === address.countryName,
    );

    setUltimateBeneficiaryByIndex(
      {
        ...formik.values,
        streetLine1: address.streetLine1,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        countryName: address.countryName,
        countryCode: uboCountry ? uboCountry.iso3 : '',
        countryId: uboCountry ? uboCountry.id : '',
      },
      beneficiaryIndex,
    );

    formik.setFieldTouched('countryCode', true);
    formik.setFieldTouched('state', true);

    // Get country states if needed
    if (address.countryCode) {
      getAllCountryStates(address.countryCode);
    }
  };

  const handleFileUploaded = async (file: IFile, docFacing: string) => {
    if (file.fileId) {
      if (docFacing === 'Front') {
        setUltimateBeneficiaryByIndex(
          {
            ...formik.values,
            identityDocumentFrontId: file.fileId,
            identityDocumentFrontBase64File: file.base64File,
            identityDocumentFrontFileName: file.fileName,
            identityDocumentFrontFileType: file.fileType,
          },
          beneficiaryIndex,
        );
      } else {
        setUltimateBeneficiaryByIndex(
          {
            ...formik.values,
            identityDocumentBackId: file.fileId,
            identityDocumentBackBase64File: file.base64File,
            identityDocumentBackFileName: file.fileName,
            identityDocumentBackFileType: file.fileType,
          },
          beneficiaryIndex,
        );
      }
    }
  };

  const resetIdentityDocument = (docFacing: string) => {
    if (docFacing === 'Front') {
      setUltimateBeneficiaryByIndex(
        {
          ...formik.values,
          identityDocumentFrontId: null,
          identityDocumentFrontBase64File: '',
          identityDocumentFrontFileName: '',
          identityDocumentFrontFileType: '',
        },
        beneficiaryIndex,
      );
    } else {
      setUltimateBeneficiaryByIndex(
        {
          ...formik.values,
          identityDocumentBackId: null,
          identityDocumentBackBase64File: '',
          identityDocumentBackFileName: '',
          identityDocumentBackFileType: '',
        },
        beneficiaryIndex,
      );
    }
  };

  const handleChange =
    (field: keyof IUltimateBeneficiary) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      // 1. Update Formik
      formik.setFieldValue(field, value);

      // 2. Update Store
      setUltimateBeneficiaryByIndex(
        {
          ...formik.values,
          [field]: value,
        },
        beneficiaryIndex,
      );
    };

  const handleValueChange =
    (field: keyof IUltimateBeneficiary) => (value: string) => {
      if (field === 'phone') {
        setMobileNumberIsTouched(true);
      }
      // 1. Update Formik
      formik.setFieldValue(field, value);

      // 2. Update Store
      setUltimateBeneficiaryByIndex(
        {
          ...formik.values,
          [field]: value,
        },
        beneficiaryIndex,
      );
    };

  const handleIdentityTypeChange = (value: number) => {
    const identityType = value
      ? IdentityTypeEnum.Id_Document
      : IdentityTypeEnum.Id_Number;

    // Reset dependent Formik fields
    formik.setFieldValue('identityType', identityType);
    formik.setFieldValue('identityNumberType', '');
    formik.setFieldValue('identityDocumentType', '');
    formik.setFieldValue('identityNumberTypeTouched', false);
    formik.setFieldValue('identityDocumentTypeTouched', false);

    // Update Store with latest values
    setUltimateBeneficiaryByIndex(
      {
        ...formik.values,
        identityType,
        identityNumberType: '',
        identityDocumentType: '',
        identityNumberTypeTouched: false,
        identityDocumentTypeTouched: false,
      },
      beneficiaryIndex,
    );
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6">
        {/* First Name */}
        <div>
          <AppInput
            type="text"
            name="firstName"
            id="firstName"
            onChange={handleChange('firstName')}
            placeholder="Enter first name"
            label="First Name"
            required
            onBlur={formik.handleBlur}
            value={formik.values.firstName}
            errorMessage={
              formik.touched.firstName && formik.errors.firstName
                ? formik.errors.firstName
                : ''
            }
          />
        </div>

        {/* Last Name */}
        <div>
          <AppInput
            type="text"
            name="lastName"
            id="lastName"
            onChange={handleChange('lastName')}
            placeholder="Enter last name"
            label="Last Name"
            required
            onBlur={formik.handleBlur}
            value={formik.values.lastName}
            errorMessage={
              formik.touched.lastName && formik.errors.lastName
                ? formik.errors.lastName
                : ''
            }
          />
        </div>

        {/* Email Address */}
        <div>
          <AppInput
            type="text"
            name="email"
            id="email"
            onChange={handleChange('email')}
            placeholder="Enter your email address"
            required
            label="Email"
            onBlur={formik.handleBlur}
            value={formik.values.email}
            errorMessage={
              formik.touched.email && formik.errors.email
                ? formik.errors.email
                : ''
            }
          />
        </div>

        {/* Phone Number */}
        <div>
          <PhoneNumberInput
            label="Phone Number"
            phone={formik.values.phone}
            onPhoneChange={handleValueChange('phone')}
            onPhoneValidCheck={(valid) => setMobileNumberIsOkay(valid)}
            required
            errorMessage={
              (mobileNumberIsTouched && !formik.values.phone) ||
              (mobileNumberIsTouched &&
                formik.values.phone &&
                !mobileNumberIsOkay)
                ? 'Please enter a valid phone number'
                : ''
            }
          />
        </div>

        {/* Date of Birth */}
        <div>
          <AppDatePicker
            label="Date of Birth"
            placeholder="Select from calendar"
            required
            value={formik.values.birthDate}
            onChange={(date) => handleDobDateChange(date)}
          />
        </div>
      </div>

      <div className="flex items-center text-sm font-semibold mt-12 mb-6">
        <span className="mr-4 text-gray-500">Beneficiary Address Details</span>
        <div className="flex-1 h-px bg-gray-300"></div>
      </div>

      {!manualEntry ? (
        <div className="col-span-2">
          <GooglePlaceAutoComplete
            label="Search Address"
            rules="required"
            id={`beneficiary-address-${Date.now()}`}
            value={formik.values?.streetLine1 || ''}
            placeholder="Enter address to search"
            onChange={handleValueChange('streetLine1')}
            onPlaceSelected={(place) => handlePlaceSelected(place)}
            onBlur={() => formik.setFieldTouched('streetLine1', true)}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6 mb-6">
          {/* Address Fields */}
          {/* Country */}
          <div>
            <CustomDropdown
              label="Select Country"
              options={filteredCountries.map((country: IActiveCountry) => {
                return {
                  name: country.iso3,
                  label: country.name,
                  countryFlag: country.flagUrl,
                  id: country.id,
                };
              })}
              emitObject
              selectedValue={formik.values.countryCode}
              onSelectChange={(value) => {
                setUltimateBeneficiaryByIndex(
                  {
                    ...formik.values,
                    countryId: value.id,
                    countryCode: value.name,
                    countryName: value.label,
                  },
                  beneficiaryIndex,
                );
                formik.setFieldValue('countryCodeTouched', true);
                getAllCountryStates(value.name);
              }}
              required
              showCountryFlag
              errorMessage={
                !formik.values.countryCode && formik.values.countryCodeTouched
                  ? 'Country is required'
                  : ''
              }
            />
          </div>

          {/* State */}
          <div>
            <CustomDropdown
              label="Select State"
              options={countryStates.map((state) => {
                return {
                  name: state.code,
                  label: state.name,
                };
              })}
              selectedValue={formik.values.state}
              onSelectChange={(value) => {
                setUltimateBeneficiaryByIndex(
                  {
                    ...formik.values,
                    state: value,
                  },
                  beneficiaryIndex,
                );
                formik.setFieldValue('stateTouched', true);
              }}
              loading={loadingResources}
              required
              errorMessage={
                !formik.values.state && formik.values.stateTouched
                  ? 'State is required'
                  : ''
              }
            />
          </div>

          {/* City */}
          <div>
            <AppInput
              type="text"
              name="city"
              id="city"
              onChange={handleChange('city')}
              placeholder="Enter city"
              required
              label="City"
              onBlur={formik.handleBlur}
              value={formik.values?.city}
              errorMessage={
                formik.touched?.city && formik.errors?.city
                  ? formik.errors?.city
                  : ''
              }
            />
          </div>

          {/* Postal Code */}
          <div>
            <AppInput
              type="text"
              name="postalCode"
              id="postalCode"
              onChange={handleChange('postalCode')}
              placeholder="Enter postal code"
              label="Postal Code"
              required
              onBlur={formik.handleBlur}
              value={formik.values?.postalCode}
              errorMessage={
                formik.touched?.postalCode && formik.errors?.postalCode
                  ? formik.errors?.postalCode
                  : ''
              }
            />
          </div>

          {/* Address Line 1, Address Line 2 */}
          <div className="col-span-2">
            <AppInput
              type="text"
              name="streetLine1"
              id="streetLine1"
              onChange={handleChange('streetLine1')}
              placeholder="Enter address line 1"
              required
              label="Address 1"
              onBlur={formik.handleBlur}
              value={formik.values?.streetLine1}
              errorMessage={
                formik.touched?.streetLine1 && formik.errors?.streetLine1
                  ? formik.errors?.streetLine1
                  : ''
              }
            />
          </div>

          <div className="col-span-2">
            <AppInput
              type="text"
              name="streetLine2"
              id="streetLine2"
              onChange={handleChange('streetLine2')}
              placeholder="Enter address line 2"
              label="Address 2"
            />
          </div>
        </div>
      )}
      {/*
      <p
        className="text-primary underline text-xs cursor-pointer"
        onClick={() => setManualEntry(!manualEntry)}
      >
        {manualEntry
          ? 'Go back to address search'
          : "Can't find your address? Click here to manually input"}
      </p>
*/}
      <div>
        <div className="flex items-center text-sm font-semibold mt-12 mb-6">
          <span className="mr-4 text-gray-500">
            Ultimate Beneficiary Document Upload
          </span>
          <div className="flex-1 h-px bg-gray-300"></div>
        </div>

        <BaseInfo text="Please provide either an identity number OR upload identity document images. Both options cannot be used simultaneously." />

        <BaseRadioGroupNew
          name={`identityType-${Date.now()}`}
          options={radioOptions}
          value={formik.values.identityType}
          onChange={(value) => handleIdentityTypeChange(Number(value))}
          className="my-6 text-sm"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6 mb-6">
          <div>
            <CustomDropdown
              label="Select ID Issuing Country"
              options={countries.map((country: IActiveCountry) => {
                return {
                  name: country.iso3,
                  label: country.name,
                  countryFlag: country.flagUrl,
                  id: country.id,
                };
              })}
              emitObject
              selectedValue={formik.values.idIssuingCountryCode}
              onSelectChange={(value) => {
                setUltimateBeneficiaryByIndex(
                  {
                    ...formik.values,
                    idIssuingCountryCode: value.name,
                    idIssuedCountryId: value.id,
                    idIssuingCountryCodeTouched: true,
                  },
                  beneficiaryIndex,
                );
              }}
              required
              showCountryFlag
              errorMessage={
                !formik.values.idIssuingCountryCode &&
                formik.values.idIssuingCountryCodeTouched
                  ? 'ID issuing Country is required'
                  : ''
              }
            />
          </div>
          <div>
            <CustomDropdown
              label={`Identity ${!formik.values.identityType ? 'Number' : 'Document'} Type`}
              options={identityDocumentTypes.map((d) => {
                return {
                  name: d.type,
                  label: toTitleCaseFromUnderscore(d.type),
                };
              })}
              selectedValue={
                !formik.values.identityType
                  ? formik.values.identityNumberType
                  : formik.values.identityDocumentType
              }
              onSelectChange={(value) => {
                if (!formik.values.identityType) {
                  setUltimateBeneficiaryByIndex(
                    {
                      ...formik.values,
                      identityNumberType: value,
                      identityNumberTypeTouched: true,
                    },
                    beneficiaryIndex,
                  );
                } else {
                  setUltimateBeneficiaryByIndex(
                    {
                      ...formik.values,
                      identityDocumentType: value,
                      identityDocumentTypeTouched: true,
                    },
                    beneficiaryIndex,
                  );
                }
              }}
              required
              errorMessage={
                !formik.values.identityType
                  ? !formik.values.identityNumberType &&
                    formik.values.identityNumberTypeTouched
                    ? 'Identity number type is required'
                    : ''
                  : !formik.values.identityDocumentType &&
                      formik.values.identityDocumentTypeTouched
                    ? 'Identity document type is required'
                    : ''
              }
            />
          </div>
        </div>

        {!formik.values.identityType && (
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6">
            <div>
              <AppInput
                type="text"
                name="identityNumber"
                id="identityNumber"
                onChange={handleChange('identityNumber')}
                placeholder="Enter Identity Number"
                label={`${formik.values?.identityNumberType ? toTitleCaseFromUnderscore(formik.values?.identityNumberType) : 'Identity Number'}`}
                required
                onBlur={formik.handleBlur}
                value={formik.values?.identityNumber}
                errorMessage={
                  formik.touched?.identityNumber &&
                  formik.errors?.identityNumber
                    ? formik.errors?.identityNumber
                    : ''
                }
              />
            </div>
          </div>
        )}

        {!!formik.values.identityType && (
          <div>
            {formik.values.identityDocumentType && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(formik.values.identityDocumentDocFacing === 1
                  ? ['Front']
                  : ['Front', 'Back']
                ).map((x) => (
                  <AppFileUpload
                    key={x}
                    type={'document'}
                    id={`upload-gov-id-${x}-${Date.now()}`}
                    uploadFile
                    documentType={toUnderscoreFromTitleCase(
                      formik.values.identityDocumentType,
                    )}
                    fileUploaded={(file) => handleFileUploaded(file, x)}
                    fileRemoved={() => resetIdentityDocument(x)}
                    label={`Upload ${x} Side`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

SingleUltimateBeneficiary.displayName = 'SingleUltimateBeneficiary';

export default SingleUltimateBeneficiary;
